import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users2, UserPlus, Check, X, MessageSquare, MapPin, Trash2, Building2, Search } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/shared";
import { listConnections, resolveConnection, removeConnection, requestConnection, searchWorkers, getUsers } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export default function Network() {
  const { currentUser, subscribe } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState(null);
  const [tab, setTab] = useState("connections");
  const [q, setQ] = useState("");
  const [discover, setDiscover] = useState([]);

  const load = () => listConnections().then(setConnections);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "pending" || t === "discover" || t === "connections") {
      setTab(t);
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const unsub = subscribe?.((ev) => {
      if (ev.type === "connection_request" || ev.type === "connection_resolved") load();
    });
    return () => unsub?.();
  }, [subscribe]);
  useEffect(() => {
    const t = setTimeout(async () => {
      const targetRole = currentUser.role === "employer" ? "worker" : "employer";
      let people = targetRole === "worker"
        ? await searchWorkers(q ? { q } : {})
        : await getUsers("employer");
      if (targetRole === "employer" && q) {
        const qq = q.toLowerCase();
        people = people.filter((p) => `${p.name} ${p.company || ""} ${p.location || ""}`.toLowerCase().includes(qq));
      }
      setDiscover(people.filter((p) => p.id !== currentUser.id));
    }, 250);
    return () => clearTimeout(t);
  }, [q, currentUser]);

  const accepted = (connections || []).filter((c) => c.status === "accepted");
  const pending = (connections || []).filter((c) => c.status === "pending" && c.direction === "incoming");
  const outgoing = (connections || []).filter((c) => c.status === "pending" && c.direction === "outgoing");

  const act = async (id, action) => {
    await resolveConnection(id, action);
    toast.success(action === "accept" ? "Connection accepted" : "Request declined");
    load();
  };
  const remove = async (id) => { await removeConnection(id); toast.success("Removed"); load(); };
  const connect = async (u) => {
    try {
      const r = await requestConnection(u.id);
      if (r.created) toast.success(`Request sent to ${u.name}`);
      else toast.info(r.status === "accepted" ? "Already connected" : "Request already pending");
      load();
    } catch { toast.error("Could not send request"); }
  };

  const connectedIds = new Set(accepted.map((c) => c.other.id));
  const outgoingIds = new Set(outgoing.map((c) => c.other.id));
  const incomingIds = new Set(pending.map((c) => c.other.id));

  return (
    <div className="min-h-screen grain">
      <Header title="Network" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-primary">
          <Users2 className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">Your network</span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Grow your circle</h1>
        <p className="mt-2 text-foreground/80 max-w-lg">Connect with employers, workers and peers. Once connected you can message each other and exchange recommendations.</p>

        <div className="mt-6 inline-flex rounded-full border border-border bg-card p-1 flex-wrap">
          <TabBtn testid="net-tab-connections" active={tab === "connections"} onClick={() => setTab("connections")}>Connections ({accepted.length})</TabBtn>
          <TabBtn testid="net-tab-pending" active={tab === "pending"} onClick={() => setTab("pending")}>
            Pending {pending.length > 0 && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 px-1.5 text-[10px] font-bold">{pending.length}</span>}
          </TabBtn>
          <TabBtn testid="net-tab-discover" active={tab === "discover"} onClick={() => setTab("discover")}>Discover</TabBtn>
        </div>

        {connections === null ? (
          <div className="mt-6 space-y-3">{[0,1,2].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
        ) : tab === "connections" ? (
          accepted.length === 0
            ? <Empty text="No connections yet. Send a request from the Discover tab." icon={<Users2 />} />
            : <div className="mt-6 space-y-3">{accepted.map((c) => (
              <PersonRow key={c.id} user={c.other} testid={`connection-${c.other.id}`}
                right={<>
                  <Button data-testid={`message-btn-${c.other.id}`} size="sm" variant="outline" onClick={() => navigate(`/messages/${c.other.id}`)}>
                    <MessageSquare className="w-4 h-4 mr-1.5" /> Message
                  </Button>
                  <Button data-testid={`remove-connection-${c.id}`} size="sm" variant="ghost" onClick={() => remove(c.id)} aria-label="Remove">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>} />
            ))}</div>
        ) : tab === "pending" ? (
          <>
            {pending.length === 0 && outgoing.length === 0
              ? <Empty text="No pending requests." icon={<UserPlus />} />
              : (
                <div className="mt-6 space-y-6">
                  {pending.length > 0 && (
                    <section>
                      <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-3">Incoming</h2>
                      <div className="space-y-3">
                        {pending.map((c) => (
                          <PersonRow key={c.id} user={c.other} note={c.message} testid={`incoming-request-${c.id}`}
                            right={<>
                              <Button data-testid={`accept-${c.id}`} size="sm" onClick={() => act(c.id, "accept")}><Check className="w-4 h-4 mr-1" /> Accept</Button>
                              <Button data-testid={`decline-${c.id}`} size="sm" variant="outline" onClick={() => act(c.id, "decline")}><X className="w-4 h-4 mr-1" /> Decline</Button>
                            </>} />
                        ))}
                      </div>
                    </section>
                  )}
                  {outgoing.length > 0 && (
                    <section>
                      <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-3">Sent</h2>
                      <div className="space-y-3">
                        {outgoing.map((c) => (
                          <PersonRow key={c.id} user={c.other} testid={`outgoing-request-${c.id}`}
                            right={<Button size="sm" variant="ghost" onClick={() => remove(c.id)}>Cancel</Button>} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
          </>
        ) : (
          <div className="mt-6">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="discover-search" placeholder="Search people to connect with…" className="pl-9"
                     value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="mt-4 space-y-3">
              {discover.length === 0 && <Empty text="No matches. Try another search." icon={<Search />} />}
              {discover.map((u) => {
                let btn = null;
                if (connectedIds.has(u.id)) btn = <Button size="sm" variant="outline" disabled><Check className="w-4 h-4 mr-1" /> Connected</Button>;
                else if (outgoingIds.has(u.id)) btn = <Button size="sm" variant="outline" disabled>Pending</Button>;
                else if (incomingIds.has(u.id)) btn = <Button size="sm" onClick={() => act((connections.find((c) => c.other.id === u.id)).id, "accept")}>Accept</Button>;
                else btn = <Button data-testid={`connect-${u.id}`} size="sm" onClick={() => connect(u)}><UserPlus className="w-4 h-4 mr-1" /> Connect</Button>;
                return <PersonRow key={u.id} user={u} testid={`discover-${u.id}`} right={btn} />;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ testid, active, onClick, children }) => (
  <button data-testid={testid} onClick={onClick}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
    {children}
  </button>
);

const PersonRow = ({ user, right, note, testid }) => {
  const navigate = useNavigate();
  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={testid}
                className="rounded-lg border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <button onClick={() => user.role === "worker" ? navigate(`/resume/${user.id}`) : null}
              className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <Avatar className="w-12 h-12 border border-border">
          <AvatarImage src={user.avatar_url} className="object-cover" />
          <AvatarFallback>{user.name[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="font-heading font-bold truncate">{user.name}</div>
          <div className="text-sm text-muted-foreground truncate">{user.headline || user.company}</div>
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate">
            {user.role === "employer" ? <Building2 className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
            {user.role === "employer" ? user.company : user.location}
          </div>
          {note && <div className="text-xs text-muted-foreground mt-1 italic">"{note}"</div>}
        </div>
      </button>
      <div className="flex gap-2 shrink-0">{right}</div>
    </motion.div>
  );
};

const Empty = ({ text, icon }) => (
  <div className="mt-10 text-center text-muted-foreground">
    <div className="w-10 h-10 mx-auto opacity-60">{icon}</div>
    <p className="mt-3">{text}</p>
  </div>
);
